import { useState } from "react";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CouponType } from "../../enums/CouponType";

/**
 * CouponAppPreview
 * ----------------
 * A web approximation of how a coupon appears on the customer mobile-app
 * coupon card (CouponMetaShowV2 / coupon wallet). It is meant to be shown
 * side-by-side with the Create / Edit forms so admins can see the live result
 * of the customer-facing fields they edit.
 *
 * Only the fields that actually drive the app card are accepted as props:
 *   - couponDescription → the bold heading on the card
 *   - couponType + couponValue → the "Save Up to X% / flat ₹X" line
 *   - couponCode → the dashed code chip
 *   - minimumOrderValue + termsAndConditions → the expandable "Details" block
 *   - errorMessages + isDisabled → the greyed-out / disabled state
 *   - discountCouponSkuId → the small "Packs" badge (not editable in the form)
 *
 * NOTE: couponHeader, couponDisplayString, productType, dates, limits, cashback
 * and the inclusion/exclusion rules do NOT change this particular card.
 */

const ORANGE = "#FF6A00";
const ORANGE_TEXT = "#E8590C";
const GREY = "#9AA0A6";

interface CouponAppPreviewProps {
  couponCode?: string;
  couponType?: string;
  couponValue?: string | number;
  couponDescription?: string;
  minimumOrderValue?: string | number;
  termsAndConditions?: string[];
  errorMessages?: string;
  discountCouponSkuId?: number | string | null;
  /** Preview the "not applicable" state the app shows when a coupon is disabled. */
  isDisabled?: boolean;
  /** Show the "Customer App Preview" caption above the card. Hide it when a parent already labels the section. */
  showLabel?: boolean;
}

const CouponAppPreview = ({
  couponCode,
  couponType,
  couponValue,
  couponDescription,
  minimumOrderValue,
  termsAndConditions,
  errorMessages,
  discountCouponSkuId,
  isDisabled: isDisabledProp = false,
  showLabel = true,
}: CouponAppPreviewProps) => {
  const [showTnc, setShowTnc] = useState(true);
  const [isDisabled, setIsDisabled] = useState(isDisabledProp);

  const tnc = (termsAndConditions ?? []).filter((t) => t && t.trim().length > 0);
  const minOrder = Number(minimumOrderValue);
  const hasDetails = minOrder > 0 || tnc.length > 0;

  const savingText = isDisabled
    ? errorMessages || "This coupon can't be applied"
    : `Save ${
        couponType === CouponType.PERCENT
          ? `Up to ${Number(couponValue || 0).toFixed(0)}%`
          : `flat ₹${Number(couponValue || 0).toFixed(0)}`
      } with this coupon`;

  return (
    <Box>
      {showLabel && (
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary" }}
        >
          📱 Customer App Preview
        </Typography>
      )}

      {/* Phone-ish frame */}
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 4,
          bgcolor: "#F2F3F5",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* ── Coupon card ── */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 3,
            border: "1px solid #ECECEC",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          {/* Header: icon + heading + saving text */}
          <Box sx={{ display: "flex", gap: 1.25, p: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isDisabled ? "#F1F1F1" : "rgba(255,106,0,0.12)",
              }}
            >
              <LocalFireDepartmentIcon sx={{ color: isDisabled ? GREY : ORANGE }} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: isDisabled ? GREY : "#1A1A1A",
                    lineHeight: 1.3,
                  }}
                >
                  {couponDescription || <span style={{ fontStyle: "italic", color: GREY }}>Coupon description…</span>}
                </Typography>
                {discountCouponSkuId ? (
                  <Box sx={{ px: 0.75, py: 0.1, borderRadius: 1, bgcolor: "#EEF2FF" }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#3949AB" }}>Packs</Typography>
                  </Box>
                ) : null}
              </Box>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: isDisabled ? GREY : ORANGE_TEXT,
                  lineHeight: 1.35,
                }}
              >
                {savingText}
              </Typography>
            </Box>
          </Box>

          {/* Code + Details toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              pb: hasDetails && showTnc ? 1 : 1.5,
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                border: "1px dashed",
                borderColor: ORANGE,
                bgcolor: "#FFF4EC",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace", letterSpacing: 1, color: "#1A1A1A" }}>
                {couponCode || "CODE"}
              </Typography>
            </Box>

            {hasDetails && (
              <Box
                onClick={() => setShowTnc((s) => !s)}
                sx={{ display: "flex", alignItems: "center", gap: 0.25, cursor: "pointer", userSelect: "none" }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1A1A1A" }}>Details</Typography>
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 18, transition: "transform 0.2s", transform: showTnc ? "rotate(180deg)" : "none" }}
                />
              </Box>
            )}
          </Box>

          {/* Expandable T&C */}
          {hasDetails && showTnc && (
            <Box sx={{ px: 1.5, pb: 1.25, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {minOrder > 0 && (
                <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
                  {`• Minimum order value: ₹${minOrder.toFixed(0)}`}
                </Typography>
              )}
              {tnc.map((t, i) => (
                <Typography key={i} sx={{ fontSize: 11, color: "#6B7280" }}>
                  {`• ${t}`}
                </Typography>
              ))}
            </Box>
          )}

          {/* Apply CTA */}
          <Box
            sx={{
              m: 1.5,
              mt: 0,
              py: 1,
              borderRadius: 1.5,
              textAlign: "center",
              bgcolor: isDisabled ? "#EDEDED" : ORANGE,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDisabled ? GREY : "#fff" }}>
              Tap to Apply
            </Typography>
          </Box>
        </Box>
      </Box>

      <FormControlLabel
        sx={{ mt: 0.5, ml: 0, gap: 0.5 }}
        control={
          <Switch size="small" checked={isDisabled} onChange={(e) => setIsDisabled(e.target.checked)} />
        }
        label={
          <Typography variant="caption" color="text.secondary">
            Preview "not applicable" state (shows error message)
          </Typography>
        }
      />
    </Box>
  );
};

export default CouponAppPreview;
